import { Trans, t } from '@lingui/macro';
import { Loader2, ChevronRight, Bookmark, Menu as MenuIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Favorite } from '../../models/Favorite';
import { Message } from '../../models/Message';
import { Room } from '../../models/Room';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import MessageItem from '../components/MessageItem';
import { UIMessage } from '../components/UIMessage';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

var ALL_ROOMS = 'all-rooms';

const FavoritesPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string; }>();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const { rooms, activeWorkspaceId, setIsMobileMenuOpen } = useAppUIContext();
  const [room, setRoom] = useState<Room>();
  const [favs, setFavs] = useState<Favorite[]>([]);
  const [myNotifCount, setMyNotifCount] = useState(0);

  const roomName = room ? Room.getRoomNameWithSharp(room, My.user.id) : '';

  useEffect(() => {
    setMyNotifCount(MyNotifs.NrOfNew);
    const interval = setInterval(() => {
      setMyNotifCount(MyNotifs.NrOfNew);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    init(roomId || "");
  }, [roomId]);

  async function init(roomId: string) {
    setRoom(rooms.find(r => r.id === roomId));
    setInProgress(true);
    let allFavs = await Fire.msg.getFavorites(My.user.id, roomId == ALL_ROOMS ? null : roomId);
    // all-rooms인 경우 현재 워크스페이스의 방만 필터링
    if (roomId == ALL_ROOMS && activeWorkspaceId) {
      const wsRoomIds = new Set(rooms.filter(r => r.workspaceId === activeWorkspaceId).map(r => r.id));
      allFavs = allFavs.filter(f => wsRoomIds.has(f.msg.roomId));
    }
    setFavs(allFavs);
    setInProgress(false);
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
      <header className="bg-background border-b px-2 md:px-6 py-2 md:py-4 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden relative"
            onClick={() => setIsMobileMenuOpen(true)}
            data-cy='menu'
          >
            <MenuIcon className="w-6 h-6" />
            {myNotifCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-destructive hover:bg-destructive/90 text-meta-sm">
                {myNotifCount > 9 ? '9+' : myNotifCount}
              </Badge>
            )}
          </Button>
          <h1 className="text-lg md:text-xl font-bold">
            {roomName ? roomName : <Trans>Saved Messages</Trans>}
          </h1>
          {inProgress && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto w-full max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        {favs.length === 0 && !inProgress && (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Bookmark className="w-16 h-16 mb-4 opacity-20" />
            <p className="text-lg font-medium"><Trans>No saved messages yet.</Trans></p>
            <p className="text-sm mt-1"><Trans>Save messages by clicking the bookmark icon in any chat.</Trans></p>
          </div>
        )}

        {favs.map((f) => (
          <div key={f.id} className="bg-background rounded-lg border shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <FavoriteItem fav={f} showRoom={roomId == ALL_ROOMS} />
          </div>
        ))}
      </main>
    </div>
  );
}

const FavoriteItem: React.FC<{ fav: Favorite, showRoom: boolean }> = ({ fav, showRoom }) => {
  const [m] = useState<UIMessage>(new UIMessage(fav.msg, []));
  const history = useHistory();

  function getRoomName() {
    const room = My.Rooms.get(m.msg.roomId);
    if (room) {
      return Room.getRoomNameWithSharp(room, My.user.id);
    } else {
      return m.msg.roomId;
    }
  }

  function emptyUIMessage() {
    return new UIMessage(new Message(new Room("", ""), My.user), []);
  }

  return (
    <>
      <div className="flex flex-col">
        {showRoom && (
          <div
            className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border cursor-pointer hover:bg-muted transition-colors"
            onClick={() => history.push(`/channel/${m.msg.roomId}`)}
          >
            <Badge variant="secondary" className="font-normal text-xs px-2 py-0.5">
              {getRoomName()}
            </Badge>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        )}

        <div className="p-2 message-box">
          <MessageItem
            m={m}
            state='favorite'
            onMessageClick={() => { }}
            parent={m.msg.parentMessageId ? emptyUIMessage() : undefined}
            isViewButton={true}
          />
        </div>

        {m.nonReactions().length > 0 && (
          <div className="bg-muted/50 border-t border-border p-2 pl-6">
            {m.nonReactions().map(c => (
              <div key={c.msg.id} className="mt-2 first:mt-0">
                <MessageItem m={c} state='favorite' onMessageClick={() => { }} parent={m} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default FavoritesPage;
